import { useCallback, useEffect, useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { BaseProjectSurvey } from '@trinity/chat/types/project-surveys';
import { cx } from '@trinity/chat-components';
import { ActivityIndicator } from '@trinity/chat-components/components/presentation/ActivityIndicator.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useQueryParam, NumberParam } from 'use-query-params';
import { Select } from '@/components/presentation/Select';
import { SelectedSurveyContext } from './context';
import styles from './styles.module.css';

type BaseProps = {
  classes?: {
    dropdownContainer?: string;
    dropdown?: string;
  };
  onChange?: (surveyId: number) => void;
  ActionsComponent?: React.ComponentType<ActionProps>;
} & ChildrenProps;

type ActionProps = {
  survey: BaseProjectSurvey;
};

type Props = BaseProps & IProjectId;

export const SurveyQuerySelector = ({ onChange: onChangeProp, ...props }: Props) => {
  const api = useChatApiClient();

  const [surveyId, setSurveyId] = useQueryParam('surveyId', NumberParam);

  const query = useQuery({
    queryKey: ['get:projects/surveys', {
      projectId: props.projectId,
    }],
    queryFn: () => api.projects.fetchProjectSurveys({
      projectId: props.projectId,
    })
      .then(res => {
        if (!surveyId && res.surveys.length) {
          setSurveyId(res.surveys[0].id, 'replaceIn');
        }

        return res;
      }),
    placeholderData: keepPreviousData,
  });

  const survey = useMemo(() => {
    return query.data?.surveys.find(s => s.id === surveyId);
  }, [surveyId, query.data]);

  const onChange = useCallback((surveyId: number) => {
    onChangeProp?.(surveyId);
    setSurveyId(surveyId);
  }, [setSurveyId, onChangeProp]);

  if (query.isLoading) return <ActivityIndicator />;

  return (
    <SelectedSurveyContext.Provider value={{ selectedSurvey: surveyId }}>
      <div className={styles.root}>
        <div className={cx(styles.dropdownContainer, props.classes?.dropdownContainer)}>
          <Select
            classes={{
              root: cx(props.classes?.dropdown, styles.dropdown),
            }}
            value={surveyId ?? ''}
            options={query.data.surveys?.map(s => ({ id: s.id, name: s.name })) ?? []}
            onChange={e => onChange(+e.target.value)} />
          {props.ActionsComponent && survey && <props.ActionsComponent survey={survey} />}
        </div>
        <div className={styles.children}>
          {props.children}
        </div>
      </div>
    </SelectedSurveyContext.Provider>
  );
};